import { PUDDLE_CELL_PX, cutOf, fieldAt, peakOf, type Puddle } from "./puddles.ts";
import { WET_MINUTE_MS, puddleFill, type WetState } from "./wetness.ts";

/**
 * The water in a map's puddles, as a server and a page can both ask for it (ADR-144 build note, P2.27-T20): which puddle lies
 * at a point, which lie near one, and how much water a puddle holds at a time. The amount is worked out from the ground's
 * wetness (`WetState`) and the puddle's own seeded onset, then changed by the overrides in force, so what a server says
 * happened to a puddle (drained, taken from) and what the weather did compose to the same number on every page. Nothing here
 * reads a clock, draws or sends anything: the caller supplies the time and the wetness. Pure.
 */

/**
 * The minutes a drained puddle takes to fill again once the weather is putting water in it.
 */
export const REFILL_MINUTES = 10;

/**
 * What happened to one puddle that the weather does not know: the time it was drained at, in milliseconds since the Unix
 * epoch, after which it refills over `REFILL_MINUTES`; and an amount taken from it, 0 to 1, taken off whatever the weather
 * leaves. Either may be absent.
 */
export interface PuddleOverride {
  readonly drainedAt?: number;
  readonly taken?: number;
}

/**
 * The overrides in force, by puddle key. A map with no entry for a puddle leaves the weather's amount as it is.
 */
export type PuddleOverrides = ReadonlyMap<string, PuddleOverride>;

/**
 * No overrides.
 */
export const NO_OVERRIDES: PuddleOverrides = new Map();

/**
 * A map's puddles, indexed for the queries.
 */
export interface PuddleIndex {
  readonly mapId: string;
  readonly puddles: readonly Puddle[];
  readonly byKey: ReadonlyMap<string, Puddle>;
  readonly buckets: ReadonlyMap<number, readonly Puddle[]>;
}

/**
 * One puddle with the water it holds at a time.
 */
export interface PuddleWater {
  readonly puddle: Puddle;
  readonly amount: number;
}

const BUCKET_PX = 512;
const BUCKET_STRIDE = 0x10000;

/**
 * The bucket key of a bucket column and row.
 *
 * @param bx Column.
 * @param by Row.
 * @returns The key.
 */
function bucketKey(bx: number, by: number): number {
  return (by + 1) * BUCKET_STRIDE + bx + 1;
}

/**
 * Indexes a map's puddles by key and by a grid of buckets 512 pixels wide.
 *
 * @param mapId The map's id.
 * @param puddles The map's puddles (`placePuddles`).
 * @returns The index.
 */
export function indexPuddles(mapId: string, puddles: readonly Puddle[]): PuddleIndex {
  const byKey = new Map<string, Puddle>();
  const buckets = new Map<number, Puddle[]>();
  for (const puddle of puddles) {
    byKey.set(puddle.key, puddle);
    const key = bucketKey(Math.floor(puddle.x / BUCKET_PX), Math.floor(puddle.y / BUCKET_PX));
    const list = buckets.get(key);
    if (list === undefined) buckets.set(key, [puddle]);
    else list.push(puddle);
  }
  return { mapId, puddles, byKey, buckets };
}

/**
 * How much water a puddle holds: the weather's fill, held under what the override leaves.
 *
 * @param state The ground's wetness at the time.
 * @param puddle The puddle.
 * @param override What happened to it that the weather does not know, if anything.
 * @param epochMs The time in milliseconds since the Unix epoch.
 * @returns 0 for a dry puddle to 1 for one full.
 */
export function waterAmount(state: WetState, puddle: Puddle, override: PuddleOverride | undefined, epochMs: number): number {
  let amount = puddleFill(state, puddle.cls, puddle.unit);
  if (override === undefined) return amount;
  if (override.drainedAt !== undefined) {
    const refilled = (epochMs - override.drainedAt) / (REFILL_MINUTES * WET_MINUTE_MS);
    amount = Math.min(amount, refilled <= 0 ? 0 : refilled >= 1 ? 1 : refilled);
  }
  if (override.taken !== undefined) amount = Math.max(0, amount - override.taken);
  return amount;
}

/**
 * Everything a query needs: the index, the wetness at a time and the overrides in force.
 */
export interface PuddleWorld {
  readonly index: PuddleIndex;
  readonly wetAt: (epochMs: number) => WetState;
  readonly overrides: PuddleOverrides;
}

/**
 * How much water the puddle of a key holds at a time.
 *
 * @param world The map's puddles, wetness and overrides.
 * @param key The puddle's key.
 * @param epochMs The time in milliseconds since the Unix epoch.
 * @returns The amount, 0 to 1; 0 for a key the map has none of.
 */
export function amountOf(world: PuddleWorld, key: string, epochMs: number): number {
  const puddle = world.index.byKey.get(key);
  return puddle === undefined ? 0 : waterAmount(world.wetAt(epochMs), puddle, world.overrides.get(key), epochMs);
}

/**
 * Whether a point lies in the water of a puddle at an amount: inside its shape cut for that amount.
 *
 * @param puddle The puddle.
 * @param amount How much water it holds.
 * @param x World x in pixels.
 * @param y World y in pixels.
 * @returns True in the water.
 */
export function inWater(puddle: Puddle, amount: number, x: number, y: number): boolean {
  if (amount <= 0 || Math.abs(x - puddle.x) > puddle.radius || Math.abs(y - puddle.y) > puddle.radius) return false;
  const cellX = Math.floor(x / PUDDLE_CELL_PX);
  const cellY = Math.floor(y / PUDDLE_CELL_PX);
  return fieldAt(puddle, cellX * PUDDLE_CELL_PX + 1, cellY * PUDDLE_CELL_PX + 1, cellX, cellY) > cutOf(amount, peakOf(puddle));
}

/**
 * The puddle whose water covers a point at a time, and how much it holds.
 *
 * @param world The map's puddles, wetness and overrides.
 * @param x World x in pixels.
 * @param y World y in pixels.
 * @param epochMs The time in milliseconds since the Unix epoch.
 * @returns The puddle and its amount, or null on dry ground.
 */
export function puddleAt(world: PuddleWorld, x: number, y: number, epochMs: number): PuddleWater | null {
  const state = world.wetAt(epochMs);
  const list = world.index.buckets.get(bucketKey(Math.floor(x / BUCKET_PX), Math.floor(y / BUCKET_PX)));
  const own = list === undefined ? [] : list;
  const neighbours: Puddle[] = [...own];
  for (let by = -1; by <= 1; by += 1) {
    for (let bx = -1; bx <= 1; bx += 1) {
      if (bx === 0 && by === 0) continue;
      const other = world.index.buckets.get(bucketKey(Math.floor(x / BUCKET_PX) + bx, Math.floor(y / BUCKET_PX) + by));
      if (other !== undefined) neighbours.push(...other);
    }
  }
  for (const puddle of neighbours) {
    const amount = waterAmount(state, puddle, world.overrides.get(puddle.key), epochMs);
    if (inWater(puddle, amount, x, y)) return { puddle, amount };
  }
  return null;
}

/**
 * The puddles within a distance of a point that hold any water at a time, nearest first.
 *
 * @param world The map's puddles, wetness and overrides.
 * @param x World x in pixels.
 * @param y World y in pixels.
 * @param radius The distance in pixels, to a puddle's centre less its own radius.
 * @param epochMs The time in milliseconds since the Unix epoch.
 * @returns The puddles and their amounts.
 */
export function puddlesNear(world: PuddleWorld, x: number, y: number, radius: number, epochMs: number): PuddleWater[] {
  const state = world.wetAt(epochMs);
  const found: { water: PuddleWater; distance: number }[] = [];
  const reach = radius + 256;
  const x0 = Math.floor((x - reach) / BUCKET_PX);
  const x1 = Math.floor((x + reach) / BUCKET_PX);
  const y0 = Math.floor((y - reach) / BUCKET_PX);
  const y1 = Math.floor((y + reach) / BUCKET_PX);
  for (let by = y0; by <= y1; by += 1) {
    for (let bx = x0; bx <= x1; bx += 1) {
      for (const puddle of world.index.buckets.get(bucketKey(bx, by)) ?? []) {
        const distance = Math.hypot(puddle.x - x, puddle.y - y);
        if (distance - puddle.radius > radius) continue;
        const amount = waterAmount(state, puddle, world.overrides.get(puddle.key), epochMs);
        if (amount > 0) found.push({ water: { puddle, amount }, distance });
      }
    }
  }
  found.sort((a, b) => a.distance - b.distance || (a.water.puddle.key < b.water.puddle.key ? -1 : 1));
  return found.map((entry) => entry.water);
}
